import classNames from "classnames";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import type { TextEntry } from "./TopperWithoutReg";
import { usePageInfoContext } from "./usePageInfoContext";

type TextProps<
  K extends string,
  C extends { [key in K]: Array<TextEntry> } = { [key in K]: Array<TextEntry> },
> = {
  textCollection?: C;
  textEntryIdent?: K;
  className?: string;
  oldText: string | undefined | null;
  textWithHtml?: boolean;
  /**
   * Оборачивать ли в <ReactMarkdown> или вставлять как обычный текст.
   * В случае если у текста указан textAsHtml, то не будем оборачивать в
   * <ReactMarkdown>, а вместо <div> обернем в <span>.
   */
  simple?: boolean;
  /**
   * Если component текста не совпадает, то отбрасываем вариант.
   * Если нет совпадений, то выбираем среди текстов без component.
   * Если совпадает либо component либо pageSlug, то отдаем предпочтение совпадению component.
   */
  component?: string;
  /**
   * Если pageSlug текста не совпадает, то отбрасываем вариант.
   * Если нет совпадений, то выбираем среди текстов без pageSlug.
   * Если совпадает либо component либо pageSlug, то отдаем предпочтение совпадению component.
   * Если параметр не передан, то берется из контекста.
   */
  pageSlug?: string;
};

/**
 * Позволяет достать наиболее подходящий текст из специально подготовленной
 * коллекции.
 * В случае отсутствия текста в коллекции будет отображен oldText.
 * @deprecated
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const Text = <K extends string>(props: TextProps<K>) => {
  const {
    className,
    oldText,
    simple,
    textCollection,
    textEntryIdent,
    component,
  } = props;

  let pageSlug = props.pageSlug;

  const pageInfo = usePageInfoContext();

  if (pageSlug == null) {
    const path = pageInfo.url.toString().split("/");
    pageSlug =
      path[path.length - 1] === ""
        ? path[path.length - 2]
        : path[path.length - 1];
  }

  let entries =
    (textCollection && textEntryIdent && textCollection[textEntryIdent]) ?? [];

  // Убираем переводы для других компонентов.
  entries = entries.filter(
    (entry) =>
      !entry.attributes.component || entry.attributes.component === component,
  );

  // Убираем переводы для других страниц.
  entries = entries.filter(
    (entry) =>
      !entry.attributes.pageSlug || entry.attributes.pageSlug === pageSlug,
  );

  // Дальше будем пытаться отфильтровывать варианты без указания компонента или страницы.

  const onlyEqualComponent = entries.filter(
    (entry) => !component || entry.attributes.component === component,
  );

  if (onlyEqualComponent.length) {
    entries = onlyEqualComponent;
  }

  const onlyEqualPageSlug = entries.filter(
    (entry) => !pageSlug || entry.attributes.pageSlug === pageSlug,
  );

  if (onlyEqualPageSlug.length) {
    entries = onlyEqualPageSlug;
  }

  const textEntry = entries[0];
  const text = (textEntry?.attributes.text || oldText) ?? "";
  const textAsHtml: boolean = textEntry?.attributes.textAsHtml;
  const mobileText = textEntry?.attributes.mobileText;
  const mobileTextAsHtml: boolean = textEntry?.attributes.mobileTextAsHtml;

  if (simple) {
    return (
      <span
        data-text-ident={textEntryIdent}
        data-text-component={component}
        className={classNames("text-wrapper", mobileText && "has-mobile-text")}
      >
        {textAsHtml ? (
          <span
            className={classNames("base-text", className)}
            dangerouslySetInnerHTML={{ __html: text }}
          />
        ) : (
          <span className={classNames("base-text", className)}>{text}</span>
        )}
        {mobileText &&
          (mobileTextAsHtml ? (
            <span
              className={classNames("mobile-text", className)}
              dangerouslySetInnerHTML={{ __html: mobileText }}
            />
          ) : (
            <span className={classNames("mobile-text", className)}>
              {mobileText}
            </span>
          ))}
      </span>
    );
  }

  return (
    <span
      data-text-ident={textEntryIdent}
      data-text-component={component}
      className={classNames("text-wrapper", mobileText && "has-mobile-text")}
    >
      {textAsHtml || props.textWithHtml ? (
        <div
          className={classNames("base-text", className)}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      ) : (
        <ReactMarkdown className={classNames("base-text", className)}>
          {text}
        </ReactMarkdown>
      )}
      {mobileText &&
        (mobileTextAsHtml || props.textWithHtml ? (
          <div
            className={classNames("mobile-text", className)}
            dangerouslySetInnerHTML={{ __html: mobileText }}
          />
        ) : (
          <ReactMarkdown
            className={classNames("mobile-text", className)}
            remarkPlugins={[remarkGfm]}
          >
            {mobileText}
          </ReactMarkdown>
        ))}
    </span>
  );
};
